import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import {
  Box,
  Dialog,
  IconButton,
  Typography,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  makeStyles,
  withStyles,
  CircularProgress,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

import { PMSteppers } from "./steps";
import { BOPSteppers } from "./bopSteps";
import { COLORS } from "../../../theme/colors";
import { Image } from "../../../components/base";
import endPoints from "../../../services/apiEndPoints";
import { validateObject } from "../../../services/utils";
import { saveNotification } from "../../../redux/notification/actions";
import location from "../../../assets/images/icons/workspace/location.svg";
import { getData, getErrorMessage, putData } from "../../../services";
import { ONBOARDING_STATUES } from "../../../services/constants";

const useStyles = makeStyles(() => ({
  container: {
    borderRadius: 8,
    "& .MuiDialog-paperWidthSm": {
      maxWidth: 995,
      backgroundColor: COLORS.white,
    },
  },
  topBox: {
    display: "flex",
    justifyContent: "space-between",
  },
  topHeading: {
    fontSize: 24,
    fontWeight: 700,
  },
  midBox: {
    display: "flex",
    marginTop: 14,
  },
  midHeading: {
    fontSize: 18,
    fontWeight: 400,
  },
  icon: {
    marginRight: 10,
  },
  savingIcon: {
    position: "absolute",
    top: "50%",
    left: 0,
    right: 0,
    margin: "auto",
  },
}));

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiDialogContent);

export const updateOnboardingStatus = (buildingId, status) => {
  putData(endPoints.OB_UPDATE_STATUS.replace("{id}", buildingId), () => {}, {
    onboarding_status: status,
  });
};

export const VALIDATION_ERROR_MESSAGE =
  "Some of the fields data is missing. Please make sure all data is filled";

export const PMFlow = ({ selectedBuilding, onClose }) => {
  const classes = useStyles();

  const dispatch = useDispatch();
  const [building, setBuilding] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (!selectedBuilding?.id) return;
    setShowSuccess(false);
    setLoading(true);
    const url = endPoints.OB_BUILDING_MANAGER.replace("{id}", selectedBuilding.id);

    getData(url, (data) => setBuilding(data))
      .catch((e) => {
        onClose();
        onError(e);
      })
      .finally(() => setLoading(false));
  }, [selectedBuilding]);

  const onBuildingChange = (key, value, pairs = {}) => {
    if (key) {
      setBuilding({
        ...building,
        [key]: value,
      });
    } else {
      setBuilding({
        ...building,
        ...pairs,
      });
    }
  };

  const onError = (e) => {
    dispatch(saveNotification({ message: getErrorMessage(e), error: true }));
  };

  const validateSavedData = (data) => {
    return validateObject(data, ["name", "address"]);
  };

  const onDone = (isDone = false) => {
    const data = {
      ...building,
      operators: building.operators?.filter((o) => o.email),
      is_done: isDone,
    };

    if (saving) return;
    setSaving(true);
    const url = endPoints.OB_BUILDING_MANAGER.replace("{id}", selectedBuilding.id);
    putData(
      url,
      (savedData) => {
        if (isDone) {
          if (validateSavedData(data)) {
            setShowSuccess(true);
            updateOnboardingStatus(selectedBuilding.id, ONBOARDING_STATUES.pending);
            setTimeout(() => onClose(), 3000);
          } else {
            if (data.operators?.length > 0) {
              setBuilding({ ...data, ...savedData });
            }
            dispatch(saveNotification({ message: VALIDATION_ERROR_MESSAGE, error: true }));
          }
        } else {
          onClose();
        }
      },
      data
    )
      .catch((e) => {
        dispatch(saveNotification({ message: getErrorMessage(e), error: true }));
      })
      .finally(() => setSaving(false));
  };

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={!!selectedBuilding}
      className={classes.container}
    >
      {loading ? (
        <Box width={400} height={500} display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <DialogTitle id="customized-dialog-title" onClose={onClose} className={classes.topBox}>
            <Typography className={classes.topHeading}>{building.name}</Typography>
            <Box className={classes.midBox}>
              <Image src={location} height={26} weight={20} className={classes.icon} />
              <Typography className={classes.midHeading}>{building.address}</Typography>
            </Box>
          </DialogTitle>
          <DialogContent id="content">
            <PMSteppers
              building={building}
              setOpen={onClose}
              onBuildingChange={onBuildingChange}
              onDone={onDone}
              showSuccess={showSuccess}
            />
            {saving && <CircularProgress className={classes.savingIcon} size="4rem" />}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
};

export const BOPFlow = ({ open, selectedBuilding, onClose }) => {
  const classes = useStyles();

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const onError = (e, message = null) => {
    dispatch(saveNotification({ message: e ? getErrorMessage(e) : message, error: true }));
  };

  return (
    <Dialog onClose={onClose} open={open} className={classes.container}>
      <DialogTitle onClose={onClose} className={classes.topBox}>
        <Typography className={classes.topHeading}>{selectedBuilding?.name}</Typography>
        <Box className={classes.midBox}>
          <Image src={location} height={26} weight={20} className={classes.icon} />
          <Typography className={classes.midHeading}>{selectedBuilding?.address}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent id="bop-container">
        <BOPSteppers
          building={selectedBuilding}
          setLoading={setLoading}
          onError={onError}
          onClose={onClose}
        />
        {loading && <CircularProgress className={classes.savingIcon} size="4rem" />}
      </DialogContent>
    </Dialog>
  );
};
